import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, from, Observable, of, Subject } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import Talk from 'talkjs';

import { getActivityDetailRouteName } from '~/attendee/helpers/activity/activity-detail-route-name.helper';
import { SESSION_STORAGE } from '~/auth/auth.module';
import { UserService } from '~/auth/services/user/user.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { MessagingUserInterface } from '~/messaging/interfaces/user.interface';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

import { environment } from '~environments/environment';

export const MESSAGING_NOTIFICATION_KEY = 'messaging-notifications';
export const ACTIVITY_STORAGE_KEY = 'talkjs-activity';

@Injectable({
  providedIn: 'root',
})
export class TalkService {
  private readonly baseUrl = '/api/talkjs';
  private readonly notificationSubject$ = new Subject<number>();
  private currentUser: Talk.User;

  constructor(
    private readonly userService: UserService,
    private readonly httpClient: HttpClient,
    private readonly localeService: LocaleService,
    private readonly localizeRoute: LocalizeRouterService,
    public readonly toggleService: ToggleService,
    private readonly translateService: TranslateService,
    @Inject(SESSION_STORAGE) private readonly storage: Storage
  ) {}

  private createUser(user: MessagingUserInterface): Observable<Talk.User> {
    return this.isReady().pipe(
      map(
        () =>
          new Talk.User({
            id: user.identifier,
            name: user.name,
            email: user.email,
            locale: this.localeService.dateLocale.code,
            role: `user_${environment.tenant}`,
          })
      )
    );
  }

  private getOrCreateConversation(
    session: Talk.Session,
    otherUser: MessagingUserInterface
  ): Observable<Talk.ConversationBuilder> {
    return this.createUser(otherUser).pipe(
      map((createdOtherUser) => {
        const activityData = JSON.parse(
          this.storage.getItem(ACTIVITY_STORAGE_KEY)
        );

        const conversationId = activityData
          ? `${this.currentUser.id}-${createdOtherUser.id}-${activityData.sku}`
          : Talk.oneOnOneId(this.currentUser, createdOtherUser);

        const conversation = session.getOrCreateConversation(conversationId);

        conversation.setParticipant(this.currentUser);
        conversation.setParticipant(createdOtherUser);

        conversation.setAttributes({ subject: null });

        if (!activityData) {
          return conversation;
        }

        const activityRoute = `${
          window.location.origin
        }${this.localizeRoute.translateRoute(
          getActivityDetailRouteName(activityData.type, activityData.sku)
        )}`;

        let message = this.translateService.instant(
          'messaging.welcome_message.relevant_activity',
          { activityLink: `<${activityRoute}|${activityData.name}>` }
        );

        if (activityData.timeslotIdentifier) {
          // if timeslot data available (it means the attendee is coming from his bookings), add the timeslot data to the system message
          const timeslotDate = new Date(activityData.timeslotStartDate);
          message = `${message} ${this.translateService.instant(
            'messaging.welcome_message.relevant_timeslot',
            {
              timeslotStartDate: timeslotDate.toLocaleString(
                this.currentUser.locale
              ),
            }
          )}`;
        }

        conversation.welcomeMessages = [message];

        const usesConversationSubject = this.toggleService.isEnabled(
          ToggleNameEnum.OrganizerTalkJsConversationSubject
        );

        if (usesConversationSubject) {
          conversation.setAttributes({
            subject: `<${activityRoute}|${activityData.name}>`,
            photoUrl: activityData.photoUrl,
          });
        }

        return conversation;
      })
    );
  }

  private getSignature(): Observable<string> {
    return this.httpClient.get<string>(`${this.baseUrl}/signature`, {
      headers: {
        authorization: `Bearer ${sessionStorage.getItem('access_token')}`,
      },
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      responseType: 'text' as any,
    });
  }

  private isReady(): Observable<void> {
    return from(Talk.ready);
  }

  public createCurrentSession(): Observable<Talk.Session> {
    const loggedUser = this.userService.getUser();
    const me: MessagingUserInterface = {
      identifier: loggedUser?.identifier,
      name: loggedUser?.fullname || loggedUser?.identifier,
      email: loggedUser?.email,
    };

    return this.isReady().pipe(
      mergeMap(() => combineLatest([this.createUser(me), this.getSignature()])),
      map(([createdUser, signature]) => {
        this.currentUser = createdUser;

        return new Talk.Session({
          appId: environment.applications.talkjs.appId,
          me: createdUser,
          signature,
        });
      }),
      tap((session) => {
        this.notificationSubject$.next(
          +this.storage.getItem(MESSAGING_NOTIFICATION_KEY)
        );

        session.unreads.onChange((conversationIds) => {
          this.storage.setItem(
            MESSAGING_NOTIFICATION_KEY,
            `${conversationIds.length}`
          );
          this.notificationSubject$.next(conversationIds?.length);
        });
      })
    );
  }

  public createInbox(
    session: Talk.Session,
    recipient?: MessagingUserInterface
  ): Observable<Talk.Inbox> {
    if (!recipient) {
      return of(session.createInbox());
    }

    return this.getOrCreateConversation(session, recipient).pipe(
      map((conversation) =>
        session.createInbox({
          selected: conversation,
          feedConversationTitleMode: 'subject',
        })
      )
    );
  }

  public onNotification$(): Observable<number> {
    return this.notificationSubject$.asObservable();
  }

  public sendMessage(
    session: Talk.Session,
    recipient: MessagingUserInterface,
    message: string
  ): Observable<void> {
    return this.getOrCreateConversation(session, recipient).pipe(
      mergeMap((conversation) => conversation.sendMessage(message, {}))
    );
  }
}
